/**
 * Pure helpers that turn Provider Analytics payload slices into Excel row
 * arrays (AOA). Kept outside the React component so we can unit-test export
 * layouts without mounting the page or writing real .xlsx files.
 *
 * Design notes (layman's terms):
 * - When the Contract filter is "All", we walk each contract's sibling bucket
 *   under byAssessmentType AND keep a footer row from the aggregated "all"
 *   bucket. That matters because patients with a missing/unknown contract
 *   only land in "all", so summing typed rows would under-count.
 * - Month columns always mean the patient's createdAt calendar month (same
 *   attribution the backend uses for demographics and outcomes last-6 data).
 * - Empty contracts (all zeros / no responses) are omitted so sheets stay readable.
 */

import {
  REFERRAL_SOURCE_ALL,
  REFERRAL_SOURCE_KEYS,
  referralSourceLabel,
} from '../../../constants/referralSources';

export const AGE_ORDER = [
  'Under 18', '18-24', '25-34', '35-44', '45-54', '55-64', '65+',
  'Invalid date', 'Seen but unanswered', 'Not yet seen',
];

const DEMO_SPECIALS = new Set(['Seen but unanswered', 'Not yet seen']);

export const OUTCOMES_LAST6_METRICS = [
  { key: 'totalDecided', label: 'Total decided' },
  { key: 'positive', label: 'Positive' },
  { key: 'negative', label: 'Negative' },
  { key: 'fullyMet', label: 'Fully met' },
  { key: 'partiallyMet', label: 'Partially met' },
  { key: 'notMet', label: 'Not met' },
];

/** Order demographic categories: age buckets first, specials last. */
export function orderDemographicCategories(dimensionKey, categories) {
  const cats = [...categories];
  if (dimensionKey === 'dateOfBirth') {
    return [
      ...AGE_ORDER.filter((a) => cats.includes(a)),
      ...cats.filter((c) => !AGE_ORDER.includes(c)).sort((a, b) => a.localeCompare(b)),
    ];
  }
  return [
    ...cats.filter((c) => !DEMO_SPECIALS.has(c)).sort((a, b) => a.localeCompare(b)),
    ...cats.filter((c) => DEMO_SPECIALS.has(c)).sort((a, b) => a.localeCompare(b)),
  ];
}

function sumArray(arr) {
  return (arr || []).reduce((a, b) => a + (Number(b) || 0), 0);
}

function isAllZero(arr) {
  return !arr || arr.length === 0 || arr.every((n) => !n);
}

/**
 * Build one demographics all-time sheet as AOA.
 *
 * @param {object} args
 * @param {object} args.byAssessmentType - demographics.byAssessmentType
 * @param {string} args.dimensionKey
 * @param {string[]} args.contractOptions - org contract list (empty = no contracts)
 * @param {string} args.assessmentTypeFilter - 'all' or a specific contract
 */
export function buildDemographicsAllTimeSheet({
  byAssessmentType,
  dimensionKey,
  contractOptions = [],
  assessmentTypeFilter = 'all',
}) {
  const breakDownByContract =
    assessmentTypeFilter === 'all' && contractOptions.length > 0;

  if (!breakDownByContract) {
    const selected = assessmentTypeFilter === 'all' ? 'all' : assessmentTypeFilter;
    const dim = byAssessmentType?.[selected]?.allTime?.[dimensionKey]
      || byAssessmentType?.all?.allTime?.[dimensionKey];
    if (!dim || !dim.total_responses) return null;

    const rows = [['Category', 'Number of Responses', '% of Responses']];
    const responses = dim.responses || {};
    const ordered = orderDemographicCategories(dimensionKey, Object.keys(responses));
    ordered.forEach((cat) => {
      const count = responses[cat] || 0;
      const pct = dim.total_responses > 0
        ? ((count / dim.total_responses) * 100).toFixed(1)
        : '0.0';
      rows.push([cat, count, `${pct}%`]);
    });
    rows.push(['TOTAL', dim.total_responses, '100.0%']);
    return {
      sheetName: String(dim.display_name || dimensionKey).substring(0, 31),
      rows,
    };
  }

  // All + contracts: Category | Contract | Count | % (within contract)
  const activeContracts = contractOptions.filter((contract) => {
    const dim = byAssessmentType?.[contract]?.allTime?.[dimensionKey];
    return dim && dim.total_responses > 0;
  });
  if (activeContracts.length === 0) return null;

  const categorySet = new Set();
  activeContracts.forEach((contract) => {
    Object.keys(byAssessmentType[contract].allTime[dimensionKey].responses || {})
      .forEach((c) => categorySet.add(c));
  });
  const ordered = orderDemographicCategories(dimensionKey, [...categorySet]);
  const displayName =
    byAssessmentType[activeContracts[0]].allTime[dimensionKey].display_name
    || byAssessmentType?.all?.allTime?.[dimensionKey]?.display_name
    || dimensionKey;

  const rows = [['Category', 'Contract', 'Number of Responses', '% of Responses']];
  ordered.forEach((cat) => {
    activeContracts.forEach((contract) => {
      const dim = byAssessmentType[contract].allTime[dimensionKey];
      const count = (dim.responses || {})[cat] || 0;
      const pct = dim.total_responses > 0
        ? ((count / dim.total_responses) * 100).toFixed(1)
        : '0.0';
      rows.push([cat, contract, count, `${pct}%`]);
    });
  });

  const allTotal = byAssessmentType?.all?.allTime?.[dimensionKey]?.total_responses || 0;
  rows.push(['TOTAL', '', allTotal, '100.0%']);

  return {
    sheetName: String(displayName).substring(0, 31),
    rows,
  };
}

/**
 * Build one demographics last-6-months sheet as AOA.
 * Month = patient createdAt month (backend attribution).
 */
export function buildDemographicsLast6MonthsSheet({
  byAssessmentType,
  dimensionKey,
  contractOptions = [],
  assessmentTypeFilter = 'all',
}) {
  const allSlice = byAssessmentType?.all?.last6Months;
  const months = allSlice?.months || [];
  if (!months.length) return null;

  const breakDownByContract =
    assessmentTypeFilter === 'all' && contractOptions.length > 0;

  if (!breakDownByContract) {
    const selected = assessmentTypeFilter === 'all' ? 'all' : assessmentTypeFilter;
    const dim = byAssessmentType?.[selected]?.last6Months?.dimensions?.[dimensionKey]
      || allSlice?.dimensions?.[dimensionKey];
    if (!dim) return null;

    const headers = ['Category', ...months.map((m) => m.label), 'TOTAL'];
    const rows = [headers];
    const ordered = orderDemographicCategories(
      dimensionKey,
      Object.keys(dim.categories || {})
    );
    ordered.forEach((cat) => {
      const arr = dim.categories?.[cat] || [];
      rows.push([cat, ...months.map((_, idx) => arr[idx] || 0), sumArray(arr)]);
    });
    const totals = dim.totalPerMonth || [];
    rows.push([
      'TOTAL RESPONSES',
      ...months.map((_, idx) => totals[idx] || 0),
      sumArray(totals),
    ]);
    return {
      sheetName: String(dim.display_name || dimensionKey).substring(0, 31),
      rows,
    };
  }

  // All + contracts: Category | Contract | months… | TOTAL
  // Footer TOTAL RESPONSES comes from the aggregated "all" bucket so unknown
  // contracts are still counted in the monthly totals.
  const activeContracts = contractOptions.filter((contract) => {
    const dim = byAssessmentType?.[contract]?.last6Months?.dimensions?.[dimensionKey];
    if (!dim) return false;
    return !isAllZero(dim.totalPerMonth);
  });
  if (activeContracts.length === 0) {
    // Fall back to aggregated all-only layout if every typed bucket is empty.
    return buildDemographicsLast6MonthsSheet({
      byAssessmentType,
      dimensionKey,
      contractOptions: [],
      assessmentTypeFilter: 'all',
    });
  }

  const categorySet = new Set();
  activeContracts.forEach((contract) => {
    Object.keys(
      byAssessmentType[contract].last6Months.dimensions[dimensionKey].categories || {}
    ).forEach((c) => categorySet.add(c));
  });
  const ordered = orderDemographicCategories(dimensionKey, [...categorySet]);
  const displayName =
    byAssessmentType[activeContracts[0]].last6Months.dimensions[dimensionKey].display_name
    || allSlice?.dimensions?.[dimensionKey]?.display_name
    || dimensionKey;

  const headers = ['Category', 'Contract', ...months.map((m) => m.label), 'TOTAL'];
  const rows = [headers];
  ordered.forEach((cat) => {
    activeContracts.forEach((contract) => {
      const dim = byAssessmentType[contract].last6Months.dimensions[dimensionKey];
      const arr = dim.categories?.[cat] || [];
      rows.push([
        cat,
        contract,
        ...months.map((_, idx) => arr[idx] || 0),
        sumArray(arr),
      ]);
    });
  });

  const allTotals = allSlice?.dimensions?.[dimensionKey]?.totalPerMonth || [];
  rows.push([
    'TOTAL RESPONSES',
    '',
    ...months.map((_, idx) => allTotals[idx] || 0),
    sumArray(allTotals),
  ]);

  return {
    sheetName: String(displayName).substring(0, 31),
    rows,
  };
}

/** List dimension keys present on the selected / all allTime slice. */
export function listDemographicDimensionKeys(byAssessmentType, assessmentTypeFilter = 'all') {
  const selected = assessmentTypeFilter === 'all' ? 'all' : assessmentTypeFilter;
  const allTime =
    byAssessmentType?.[selected]?.allTime
    || byAssessmentType?.all?.allTime
    || {};
  return Object.keys(allTime);
}

/**
 * Outcomes last-6-months "Diagnostic Outcomes" sheet.
 */
export function buildOutcomesLast6MonthsSheet({
  byAssessmentType,
  contractOptions = [],
  assessmentTypeFilter = 'all',
}) {
  const allSlice = byAssessmentType?.all?.last6Months;
  const months = allSlice?.months || [];
  if (!months.length || !allSlice?.metrics) return null;

  const breakDownByContract =
    assessmentTypeFilter === 'all' && contractOptions.length > 0;

  if (!breakDownByContract) {
    const selected = assessmentTypeFilter === 'all' ? 'all' : assessmentTypeFilter;
    const metrics =
      byAssessmentType?.[selected]?.last6Months?.metrics
      || allSlice.metrics;
    const headers = ['Metric', ...months.map((m) => m.label), 'TOTAL'];
    const rows = [headers];
    OUTCOMES_LAST6_METRICS.forEach(({ key, label }) => {
      const arr = metrics?.[key] || [];
      rows.push([label, ...months.map((_, idx) => arr[idx] || 0), sumArray(arr)]);
    });
    const totals = metrics?.totalDecided || [];
    rows.push([
      'TOTAL DECIDED',
      ...months.map((_, idx) => totals[idx] || 0),
      sumArray(totals),
    ]);
    return { sheetName: 'Diagnostic Outcomes', rows };
  }

  const activeContracts = contractOptions.filter((contract) => {
    const metrics = byAssessmentType?.[contract]?.last6Months?.metrics;
    return metrics && !isAllZero(metrics.totalDecided);
  });
  if (activeContracts.length === 0) {
    return buildOutcomesLast6MonthsSheet({
      byAssessmentType,
      contractOptions: [],
      assessmentTypeFilter: 'all',
    });
  }

  const headers = ['Metric', 'Contract', ...months.map((m) => m.label), 'TOTAL'];
  const rows = [headers];
  OUTCOMES_LAST6_METRICS.forEach(({ key, label }) => {
    activeContracts.forEach((contract) => {
      const arr = byAssessmentType[contract].last6Months.metrics?.[key] || [];
      rows.push([
        label,
        contract,
        ...months.map((_, idx) => arr[idx] || 0),
        sumArray(arr),
      ]);
    });
  });

  const allTotals = allSlice.metrics.totalDecided || [];
  rows.push([
    'TOTAL DECIDED',
    '',
    ...months.map((_, idx) => allTotals[idx] || 0),
    sumArray(allTotals),
  ]);

  return { sheetName: 'Diagnostic Outcomes', rows };
}

/**
 * Outcomes all-time workbook sheets (Summary, Presentation, Severity, demo tables).
 * Returns an array of { sheetName, rows }.
 */
export function buildOutcomesAllTimeSheets({
  byAssessmentType,
  contractOptions = [],
  assessmentTypeFilter = 'all',
}) {
  const breakDownByContract =
    assessmentTypeFilter === 'all' && contractOptions.length > 0;

  const pickAllTime = (key) => {
    if (!breakDownByContract) {
      const selected = assessmentTypeFilter === 'all' ? 'all' : assessmentTypeFilter;
      return byAssessmentType?.[selected]?.allTime || byAssessmentType?.all?.allTime || null;
    }
    return null;
  };

  const sheets = [];

  if (!breakDownByContract) {
    const at = pickAllTime();
    if (!at || !at.totalDecided) return [];

    sheets.push({
      sheetName: 'Summary',
      rows: [
        ['Metric', 'Count', '%'],
        ['Total decided', at.totalDecided, '100.0%'],
        ['Positive', at.positive, at.positivePct != null ? `${at.positivePct}%` : '-'],
        ['Negative', at.negative, at.negativePct != null ? `${at.negativePct}%` : '-'],
        ['Fully met', at.fullyMet, at.totalDecided ? `${((at.fullyMet / at.totalDecided) * 100).toFixed(1)}%` : '-'],
        ['Partially met', at.partiallyMet, at.totalDecided ? `${((at.partiallyMet / at.totalDecided) * 100).toFixed(1)}%` : '-'],
      ],
    });

    if ((at.presentation || []).length) {
      sheets.push({
        sheetName: 'Presentation',
        rows: [
          ['Category', 'Count', '%'],
          ...at.presentation.map((item) => [item.label, item.count, `${item.pct}%`]),
        ],
      });
    }
    if ((at.severity || []).length) {
      sheets.push({
        sheetName: 'Severity',
        rows: [
          ['Category', 'Count', '%'],
          ...at.severity.map((item) => [item.label, item.count, `${item.pct}%`]),
        ],
      });
    }

    [
      { key: 'byAgeGroup', name: 'By Age Group' },
      { key: 'byGender', name: 'By Gender' },
      { key: 'byEthnicity', name: 'By Ethnicity' },
    ].forEach(({ key, name }) => {
      const rowsData = at[key] || [];
      if (!rowsData.length) return;
      sheets.push({
        sheetName: name.substring(0, 31),
        rows: [
          ['Category', 'Total', 'Positive', 'Negative', 'Pos %', 'Neg %'],
          ...rowsData.map((r) => [
            r.label, r.total, r.positive, r.negative,
            `${r.positivePct}%`, `${r.negativePct}%`,
          ]),
        ],
      });
    });

    return sheets;
  }

  // All + contracts
  const activeContracts = contractOptions.filter((contract) => {
    const at = byAssessmentType?.[contract]?.allTime;
    return at && at.totalDecided > 0;
  });
  if (activeContracts.length === 0) {
    return buildOutcomesAllTimeSheets({
      byAssessmentType,
      contractOptions: [],
      assessmentTypeFilter: 'all',
    });
  }

  const allAt = byAssessmentType?.all?.allTime || {};
  const summaryRows = [['Metric', 'Contract', 'Count', '%']];
  const summaryMetrics = [
    { key: 'totalDecided', label: 'Total decided', pct: () => '100.0%' },
    { key: 'positive', label: 'Positive', pct: (at) => (at.positivePct != null ? `${at.positivePct}%` : '-') },
    { key: 'negative', label: 'Negative', pct: (at) => (at.negativePct != null ? `${at.negativePct}%` : '-') },
    {
      key: 'fullyMet',
      label: 'Fully met',
      pct: (at) => (at.totalDecided ? `${((at.fullyMet / at.totalDecided) * 100).toFixed(1)}%` : '-'),
    },
    {
      key: 'partiallyMet',
      label: 'Partially met',
      pct: (at) => (at.totalDecided ? `${((at.partiallyMet / at.totalDecided) * 100).toFixed(1)}%` : '-'),
    },
  ];
  summaryMetrics.forEach(({ key, label, pct }) => {
    activeContracts.forEach((contract) => {
      const at = byAssessmentType[contract].allTime;
      summaryRows.push([label, contract, at[key] || 0, pct(at)]);
    });
  });
  summaryRows.push(['TOTAL DECIDED', '', allAt.totalDecided || 0, '100.0%']);
  sheets.push({ sheetName: 'Summary', rows: summaryRows });

  // Presentation / severity — union categories across contracts
  [
    { key: 'presentation', name: 'Presentation' },
    { key: 'severity', name: 'Severity' },
  ].forEach(({ key, name }) => {
    const cats = new Set();
    activeContracts.forEach((contract) => {
      (byAssessmentType[contract].allTime[key] || []).forEach((item) => cats.add(item.label));
    });
    if (cats.size === 0) return;
    const rows = [['Category', 'Contract', 'Count', '%']];
    [...cats].sort((a, b) => a.localeCompare(b)).forEach((cat) => {
      activeContracts.forEach((contract) => {
        const at = byAssessmentType[contract].allTime;
        const item = (at[key] || []).find((i) => i.label === cat);
        const count = item?.count || 0;
        const pct = item ? `${item.pct}%` : '0.0%';
        rows.push([cat, contract, count, pct]);
      });
    });
    sheets.push({ sheetName: name, rows });
  });

  [
    { key: 'byAgeGroup', name: 'By Age Group' },
    { key: 'byGender', name: 'By Gender' },
    { key: 'byEthnicity', name: 'By Ethnicity' },
  ].forEach(({ key, name }) => {
    const cats = new Set();
    activeContracts.forEach((contract) => {
      (byAssessmentType[contract].allTime[key] || []).forEach((r) => cats.add(r.label));
    });
    if (cats.size === 0) return;
    const rows = [['Category', 'Contract', 'Total', 'Positive', 'Negative', 'Pos %', 'Neg %']];
    const orderedCats = key === 'byAgeGroup'
      ? orderDemographicCategories('dateOfBirth', [...cats])
      : [...cats].sort((a, b) => a.localeCompare(b));
    orderedCats.forEach((cat) => {
      activeContracts.forEach((contract) => {
        const row = (byAssessmentType[contract].allTime[key] || [])
          .find((r) => r.label === cat);
        rows.push([
          cat,
          contract,
          row?.total || 0,
          row?.positive || 0,
          row?.negative || 0,
          row ? `${row.positivePct}%` : '0%',
          row ? `${row.negativePct}%` : '0%',
        ]);
      });
    });
    sheets.push({ sheetName: name.substring(0, 31), rows });
  });

  return sheets;
}

/**
 * Resolve the on-screen outcomes allTime slice, with legacy flat fallback.
 * Used by ProviderAnalytics so the panel keeps working during cache rollout.
 */
export function resolveOutcomesAllTimeSlice(outcomes, assessmentTypeFilter, showAssessmentTypeFilter) {
  const selected = showAssessmentTypeFilter ? assessmentTypeFilter : 'all';
  const byType = outcomes?.byAssessmentType;
  if (byType) {
    // A specific contract returns EXACTLY its slice (or null when it has none).
    // It must never fall back to the org-wide `all` bucket, or a zero-data
    // contract would misleadingly show every org's numbers. Only the aggregate
    // view resolves `all`.
    if (selected !== 'all') return byType[selected]?.allTime || null;
    return byType.all?.allTime || null;
  }
  // Legacy flat payload (pre-v8 cache)
  if (outcomes && outcomes.totalDecided != null) return outcomes;
  return null;
}

export function resolveOutcomesByAssessmentType(outcomes) {
  if (outcomes?.byAssessmentType) return outcomes.byAssessmentType;
  if (outcomes && outcomes.totalDecided != null) {
    return { all: { allTime: outcomes, last6Months: { months: [], metrics: {} } } };
  }
  return null;
}

// ─── WLC referral export ─────────────────────────────────────────────────────

/**
 * Map the export sheet name to the matching period key in `exportSeries`.
 * `exportSeries` is the all-time (uncapped) referral series the backend emits
 * specifically for exports, so these sheets always cover full history even
 * though the on-screen day chart is capped to the last 180 days.
 */
const WLC_REFERRAL_PERIOD_SHEETS = [
  { period: 'day', sheetName: 'By Day' },
  { period: 'week', sheetName: 'By Week' },
  { period: 'month', sheetName: 'By Month' },
];

/**
 * Pick the source slice to export. Falls back to the `all` aggregate when the
 * requested source is missing (e.g. a stale payload), so the export never
 * throws on an unexpected filter value.
 */
function resolveReferralExportSource(bySource, sourceFilter) {
  if (sourceFilter && sourceFilter !== REFERRAL_SOURCE_ALL && bySource?.[sourceFilter]) {
    return sourceFilter;
  }
  return REFERRAL_SOURCE_ALL;
}

/**
 * Build the "Summary" sheet: headline metrics plus time-to-conversion stats
 * for the selected source. Values mirror the on-screen cards so the export and
 * the UI never disagree.
 */
function buildWlcReferralSummarySheet(bySource, selected) {
  const slice = bySource[selected] || bySource[REFERRAL_SOURCE_ALL] || {};
  const metrics = slice.metrics || {};
  const rows = [
    ['Metric', 'Value'],
    ['Source', referralSourceLabel(selected)],
    ['Total referrals', metrics.totalReferrals || 0],
    ['Converted to assessment', metrics.converted || 0],
    ['Conversion rate', metrics.conversionPct != null ? `${metrics.conversionPct}%` : '--'],
    ['Avg. days to convert', metrics.avgDaysToConvert != null ? `${metrics.avgDaysToConvert}` : '--'],
  ];

  const cs = slice.conversionStats;
  if (cs) {
    rows.push(['Median days to convert', cs.median]);
    rows.push(['Min days to convert', cs.min]);
    rows.push(['Max days to convert', cs.max]);
    rows.push(['Conversions (with timing)', cs.count]);
    (cs.distribution || []).forEach((bucket) => {
      rows.push([bucket.label, `${bucket.count} (${bucket.pct}%)`]);
    });
  }
  return { sheetName: 'Summary', rows };
}

/**
 * Build one period sheet (By Day / Week / Month) for a single source:
 *   Date | Referrals | Converted   + TOTAL row.
 * Rows with zero referrals AND zero conversions are omitted so the sheet stays
 * readable (day-level history is mostly empty), but the TOTAL is summed over
 * the FULL series so it is always the true all-time figure.
 */
function buildSingleSourcePeriodSheet(exportSeries, sheetName) {
  const labels = exportSeries?.labels || [];
  const refs = exportSeries?.uniqueReferrals || [];
  const convs = exportSeries?.convertedToAssessment || [];

  const rows = [['Date', 'Referrals', 'Converted']];
  let totalRef = 0;
  let totalConv = 0;
  labels.forEach((label, i) => {
    const r = refs[i] || 0;
    const c = convs[i] || 0;
    totalRef += r;
    totalConv += c;
    if (r === 0 && c === 0) return;
    rows.push([label, r, c]);
  });
  rows.push(['TOTAL', totalRef, totalConv]);
  return { sheetName, rows };
}

/**
 * Build one period sheet for the "All sources" view:
 *   Date | Source | Referrals | Converted   + TOTAL row.
 * Every source's series shares one spine (same labels/indices from the
 * backend), so we walk the aggregate `all` series to decide which dates have
 * any activity, then emit one row per real source for those dates. The TOTAL
 * comes from the aggregate summed over the full series (true all-time).
 */
function buildAllSourcesPeriodSheet(bySource, period, sheetName) {
  const aggSeries = bySource[REFERRAL_SOURCE_ALL]?.exportSeries?.[period] || {};
  const aggLabels = aggSeries.labels || [];
  const aggRefs = aggSeries.uniqueReferrals || [];
  const aggConvs = aggSeries.convertedToAssessment || [];

  const rows = [['Date', 'Source', 'Referrals', 'Converted']];
  let totalRef = 0;
  let totalConv = 0;
  aggLabels.forEach((label, i) => {
    const aggR = aggRefs[i] || 0;
    const aggC = aggConvs[i] || 0;
    totalRef += aggR;
    totalConv += aggC;
    if (aggR === 0 && aggC === 0) return;
    REFERRAL_SOURCE_KEYS.forEach((sourceKey) => {
      const es = bySource[sourceKey]?.exportSeries?.[period] || {};
      const r = (es.uniqueReferrals || [])[i] || 0;
      const c = (es.convertedToAssessment || [])[i] || 0;
      rows.push([label, referralSourceLabel(sourceKey), r, c]);
    });
  });
  rows.push(['TOTAL', '', totalRef, totalConv]);
  return { sheetName, rows };
}

/**
 * Build the full WLC referral workbook as an array of {sheetName, rows}.
 *
 * Sheets: Summary, By Day, By Week, By Month. When a specific source is
 * selected the period sheets show that source only (3 columns); when "All" is
 * selected they break each date down by source (4 columns incl. a Source
 * column), mirroring the contract-breakdown layout used elsewhere in this
 * module. All figures come from the all-time `exportSeries`, independent of
 * the capped on-screen day chart.
 *
 * @param {object} args
 * @param {object} args.bySource - referrals.bySource ({ all, main, ... })
 * @param {string} args.sourceFilter - 'all' or a specific source key
 * @returns {Array<{sheetName: string, rows: any[][]}>}
 */
export function buildWlcReferralSheets({ bySource, sourceFilter = REFERRAL_SOURCE_ALL }) {
  if (!bySource || !bySource[REFERRAL_SOURCE_ALL]) return [];
  const selected = resolveReferralExportSource(bySource, sourceFilter);
  const breakDownBySource = selected === REFERRAL_SOURCE_ALL;

  const sheets = [buildWlcReferralSummarySheet(bySource, selected)];

  WLC_REFERRAL_PERIOD_SHEETS.forEach(({ period, sheetName }) => {
    if (breakDownBySource) {
      sheets.push(buildAllSourcesPeriodSheet(bySource, period, sheetName));
    } else {
      const exportSeries = bySource[selected]?.exportSeries?.[period];
      sheets.push(buildSingleSourcePeriodSheet(exportSeries, sheetName));
    }
  });

  return sheets;
}
